import { aadConfig, getAccessToken } from '@services/aad';
import apiProxy from '@services/api/apiProxy';
import type {
  Subscription,
  SubscriptionCheckout,
  SubscriptionCheckoutResult,
  SubscriptionCustomer,
  SubscriptionLicense,
  SubscriptionProduct,
} from '@services/dto/subscription';
import axios from 'axios';

export const getSubscriptionProducts = apiProxy(
  'getSubscriptionProducts',
  async (abort?: AbortController): Promise<SubscriptionProduct[]> => {
    await getAccessToken(aadConfig().accountScopes);

    const { data } = await axios.get<SubscriptionProduct[]>(`${process.env.ACCOUNT_HOST}/v1/subscriptions/products`, {
      signal: abort?.signal,
    });
    return data;
  },
);

export const getSubscriptionConfiguration = apiProxy(
  'getSubscriptionConfiguration',
  async (abort?: AbortController): Promise<Record<string, string | number | boolean>> => {
    await getAccessToken(aadConfig().accountScopes);

    const { data } = await axios.get<Record<string, string | number | boolean>>(
      `${process.env.ACCOUNT_HOST}/v1/subscriptions/configuration`,
      {
        signal: abort?.signal,
      },
    );
    return data;
  },
);

export const getSubscription = apiProxy(
  'getSubscription',
  async (accountId: string, abort?: AbortController): Promise<Subscription | undefined> => {
    await getAccessToken(aadConfig().accountScopes);

    try {
      const { data } = await axios.get<Subscription>(`${process.env.ACCOUNT_HOST}/v1/subscriptions/${accountId}`, {
        signal: abort?.signal,
      });
      return data;
    } catch (err) {
      return undefined;
    }
  },
);

export const upsertSubscriptionCustomer = apiProxy(
  'upsertSubscriptionCustomer',
  async (
    accountId: string,
    customer?: SubscriptionCustomer,
    abort?: AbortController,
  ): Promise<SubscriptionCustomer> => {
    await getAccessToken(aadConfig().accountScopes);

    const { data } = await axios.post<SubscriptionCustomer>(
      `${process.env.ACCOUNT_HOST}/v1/subscriptions/customers/${accountId}`,
      customer ?? {},
      {
        signal: abort?.signal,
      },
    );
    return data;
  },
);

/**
 * Opens a Stripe subscription checkout session.
 *
 * Not wrapped in apiProxy: this creates server state, and a session-creating call must never be
 * handed another caller's (possibly aborted) promise.
 */
export const createSubscriptionCheckout = async (
  accountId: string,
  checkout: SubscriptionCheckout,
  abort?: AbortController,
): Promise<SubscriptionCheckoutResult> => {
  await getAccessToken(aadConfig().accountScopes);

  const { data } = await axios.post<SubscriptionCheckoutResult>(
    `${process.env.ACCOUNT_HOST}/v1/subscriptions/checkout/${accountId}`,
    checkout,
    {
      signal: abort?.signal,
    },
  );
  return data;
};

/**
 * Waits for a freshly paid subscription to become visible.
 *
 * The webhook warms the server's subscription cache on checkout completion, so this usually
 * resolves within a couple of polls. Resolves undefined on timeout rather than throwing — the
 * payment did succeed, the record is just still settling.
 */
export const waitForSubscription = async (
  accountId: string,
  timeoutMs = 60_000,
  intervalMs = 2_000,
): Promise<Subscription | undefined> => {
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    const subscription = await getSubscription(accountId);
    if (subscription?.id || subscription?.status) {
      return subscription;
    }

    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  return undefined;
};

export const adjustSubscriptionSeats = apiProxy(
  'adjustSubscriptionSeats',
  async (accountId: string, seats: number, abort?: AbortController): Promise<Subscription> => {
    await getAccessToken(aadConfig().accountScopes);

    const { data } = await axios.post(
      `${process.env.ACCOUNT_HOST}/v1/subscriptions/${accountId}/seats`,
      {
        seats,
      },
      {
        signal: abort?.signal,
      },
    );
    return data;
  },
);

export const cancelSubscription = apiProxy(
  'cancelSubscription',
  async (accountId: string, feedback: string[], comment: string, abort?: AbortController): Promise<Subscription> => {
    await getAccessToken(aadConfig().accountScopes);

    const { data } = await axios.post(
      `${process.env.ACCOUNT_HOST}/v1/subscriptions/${accountId}/cancel`,
      {
        comment,
        feedback,
      },
      {
        signal: abort?.signal,
      },
    );
    return data;
  },
);

export const uncancelSubscription = apiProxy(
  'uncancelSubscription',
  async (accountId: string, abort?: AbortController): Promise<Subscription> => {
    await getAccessToken(aadConfig().accountScopes);

    const { data } = await axios.delete(`${process.env.ACCOUNT_HOST}/v1/subscriptions/${accountId}/cancel`, {
      signal: abort?.signal,
    });
    return data;
  },
);

/**
 * The caller's seat on this account, or undefined when they do not have one.
 *
 * A 404 here is an answer, not a fault: it is what the account service returns for an account
 * with no subscription record at all, and for a user who simply holds no licence under one. Both
 * mean the same thing to every caller — no seat — and all four of them were already written to
 * treat it that way behind a `.catch()`.
 *
 * Returning undefined instead of throwing puts that where it belongs. A throw on the ordinary
 * no-subscription case made the ordinary case indistinguishable from a real outage, and left
 * every caller responsible for a rejection that carried no information.
 *
 * A genuine failure — 401, 500, a network error — still throws, because those callers should not
 * quietly conclude "no seat" from an outage and send a subscriber to checkout.
 */
export const getSubscriptionLicense = apiProxy(
  'getSubscriptionLicense',
  async (accountId: string, userId: string, abort?: AbortController): Promise<SubscriptionLicense | undefined> => {
    await getAccessToken(aadConfig().accountScopes);

    try {
      const { data } = await axios.get(`${process.env.ACCOUNT_HOST}/v1/subscriptions/${accountId}/licenses/${userId}`, {
        signal: abort?.signal,
      });
      return data;
    } catch (err) {
      // A 404 is still read as "no seat" rather than a fault. The route now answers 200 with
      // covered:false for both no-subscription and no-licence, but an older deployment - or the
      // no-Stripe-customer path, which throws NotFound from inside the lookup - can still land
      // here, and neither means the report is free.
      if ((err as { response?: { status?: number } })?.response?.status === 404) return undefined;

      // Anything else is genuinely unknown, and the caller has to be able to tell.
      throw err;
    }
  },
);

export const getSubscriptionLicenses = apiProxy(
  'getSubscriptionLicenses',
  async (accountId: string, abort?: AbortController): Promise<SubscriptionLicense[]> => {
    await getAccessToken(aadConfig().accountScopes);

    const { data } = await axios.get(`${process.env.ACCOUNT_HOST}/v1/subscriptions/${accountId}/licenses`, {
      signal: abort?.signal,
    });
    return data;
  },
);

export const subscriptionAssignLicense = apiProxy(
  'subscriptionAssignLicense',
  async (accountId: string, userId: string, abort?: AbortController): Promise<SubscriptionLicense> => {
    await getAccessToken(aadConfig().accountScopes);

    const { data } = await axios.post(`${process.env.ACCOUNT_HOST}/v1/subscriptions/${accountId}/licenses/${userId}`, {
      signal: abort?.signal,
    });
    return data;
  },
);

export const subscriptionUnassignLicense = apiProxy(
  'subscriptionUnassignLicense',
  async (accountId: string, userId: string, abort?: AbortController): Promise<SubscriptionLicense> => {
    await getAccessToken(aadConfig().accountScopes);

    const { data } = await axios.delete(
      `${process.env.ACCOUNT_HOST}/v1/subscriptions/${accountId}/licenses/${userId}`,
      {
        signal: abort?.signal,
      },
    );
    return data;
  },
);
